import CircularProgress from "@material-ui/core/CircularProgress";
import { makeStyles, Theme } from "@material-ui/core/styles";
import { ConfigTypes } from "@rambody/commons/lib/constants";
import { formatString } from "@rambody/commons/lib/extentions/string-helper";
import React, { useEffect, useState } from "react";
import { isAndroid } from "react-device-detect";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import AppSettings from "../constants/AppSettings";
import { saveConfig } from "../extentions/config-provider";

interface MatchParams {
  packageId: string;
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    backgroundColor: theme.header.background.main,
    height: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
  },
  text: {
    color: theme.footer.text.primary,
    fontSize: theme.typography.pxToRem(20),
    textAlign: "center",
    marginBottom: theme.spacing(2),
  },
  spinner: {
    color: theme.footer.text.primary,
    marginBottom: theme.spacing(2),
  },
}));

const SharePackageContainer: React.FC = () => {
  const classes = useStyles();
  const { packageId } = useParams<MatchParams>();
  const { t } = useTranslation();
  const [isRedirecting, setIsRedirecting] = useState(false);

  useEffect(() => {
    async function handleRedirect(): Promise<void> {
      if (!packageId || isRedirecting) return;

      setIsRedirecting(true);

      try {
        // Save IP log first
        await saveConfig(ConfigTypes.IpLog, {
          packageId,
        });

        // Wait 1.5 seconds so users can see the loading screen
        await new Promise((resolve) => setTimeout(resolve, 1500));

        // Try to open the app
        window.location.href = formatString(
          AppSettings.Links.OpenPackageAppLink,
          packageId
        );

        // After 2.5 seconds, redirect to store if app didn't open
        setTimeout((): void => {
          if (isAndroid) {
            window.location.href = AppSettings.Links.GooglePlayStoreLink;
          } else {
            window.location.href = AppSettings.Links.AppStoreLink;
          }
        }, 2500);
      } catch (error) {
        // If IP logging fails, still try to open the app after a delay
        // eslint-disable-next-line no-console
        console.error("Failed to log IP:", error);

        setTimeout(() => {
          window.location.href = formatString(
            AppSettings.Links.OpenPackageAppLink,
            packageId
          );
        }, 1500);
      }
    }

    handleRedirect();
  }, [packageId, isRedirecting]);

  return (
    <div className={classes.root}>
      <CircularProgress className={classes.spinner} size={40} />
      <div className={classes.text}>{t("redirectingToApp")}</div>
      <div className={classes.text}>{t("ifNotRedirected")}</div>
    </div>
  );
};

export default SharePackageContainer;
